import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Loader2,
  Trash2,
  Download,
  FileText,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';
import { usePermission } from '@/components/guards/permission-guard';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { dateStamp } from '@/lib/download-xlsx';
import { downloadMovementsPdf, downloadMovementsXlsx } from '@/lib/cari-movements-export';
import { TRADING_COMPANY } from '@/lib/report-company';
import type { MovementDoc } from '@/lib/cari-movements-doc';
import { AddMovementDialog } from '@/components/trading/cari/add-movement-dialog';

const selectClass =
  'h-8 w-32 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

const CURRENCY_NAMES: Record<string, string> = {
  TRY: 'TÜRK LİRASI - TL',
  EUR: 'EURO - EUR',
  GBP: 'İNGİLİZ STERLİNİ - GBP',
  USD: 'AMERİKAN DOLARI - USD',
};

export function CariMovementsPage() {
  const { t, i18n } = useTranslation('trading');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const { source, id } = useParams<{ source: string; id: string }>();
  const canCreate = usePermission('CUSTOMERS', 'CREATE');
  const canDelete = usePermission('CUSTOMERS', 'DELETE');

  const [dateFrom, setDateFrom] = useSessionState('trading:cari-movements:dateFrom', '');
  const [dateTo, setDateTo] = useSessionState('trading:cari-movements:dateTo', '');
  const [currency, setCurrency] = useSessionState('trading:cari-movements:currency', '');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const src: 'customer' | 'supplier' = source === 'supplier' ? 'supplier' : 'customer';
  const utils = trpc.useUtils();

  const buildDoc = (
    res: Awaited<ReturnType<typeof utils.tradingCari.exportCariMovements.fetch>>
  ): MovementDoc => {
    const fmtDay = (val: string) => new Date(val).toLocaleDateString(dateLocale);
    const periodParts = [
      dateFrom || dateTo
        ? `${dateFrom ? fmtDay(dateFrom) : '…'} – ${dateTo ? fmtDay(dateTo) : '…'}`
        : '',
    ].filter(Boolean);
    return {
      company: TRADING_COMPANY,
      meta: {
        mode: 'statement',
        title: t('cariMovements.statementTitle', 'Account Statement').toLocaleUpperCase(
          i18n.language === 'tr' ? 'tr-TR' : 'en-US'
        ),
        periodLabel: periodParts.join('  ·  ') || t('cariMovements.allTime', 'All dates'),
        generatedLabel: t('cariMovements.exportGenerated', 'Generated {{ts}}', {
          ts: new Date().toLocaleString(dateLocale),
        }),
        params: [
          {
            label: t('cariMovements.currency', 'Currency'),
            value: currency || t('cariMovements.allCurrencies', 'All'),
          },
        ],
        columns: {
          no: '#',
          date: t('cariMovements.date', 'Date'),
          cari: t('cariTransactions.cari', 'Cari'),
          type: t('cariTransactions.type', 'Type'),
          code: t('cariTransactions.code', 'Code'),
          voucher: t('cariMovements.voucherNo', 'Voucher No'),
          description: t('cariMovements.description', 'Description'),
          currency: t('cariMovements.currency', 'Currency'),
          fxDebit: t('cariMovements.fxDebit', 'FX Debit'),
          fxCredit: t('cariMovements.fxCredit', 'FX Credit'),
          tryDebit: t('cariMovements.tryDebit', 'TL Debit'),
          tryCredit: t('cariMovements.tryCredit', 'TL Credit'),
          balance: t('cariMovements.tryBalance', 'TL Balance'),
        },
        totalLabel: t('cariMovements.total', 'TOTAL'),
        balanceLabel: t('cariMovements.balance', 'BALANCE'),
        debitSide: t('cariMovements.debitSide', '(B)'),
        creditSide: t('cariMovements.creditSide', '(A)'),
        manualTag: 'M',
        emptyLabel: t('cariMovements.noEntries', 'No movements found'),
        pageLabel: (p, tt) =>
          t('cariMovements.exportPage', 'Page {{page}} / {{total}}', { page: p, total: tt }),
        currencyNames: CURRENCY_NAMES,
        balanceNative: false, // trading ekstre shows the TL-equivalent balance under a "TL Bakiye" header
      },
      data: {
        rows: res.rows,
        totalsByCurrency: res.totalsByCurrency,
        cari: { code: res.cari.legacyCariCode ?? '', name: res.cari.companyName },
      },
    };
  };

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    if (!id) return;
    setExporting(true);
    try {
      const res = await utils.tradingCari.exportCariMovements.fetch({
        source: src,
        id,
        currency: (currency || undefined) as 'EUR' | 'GBP' | 'USD' | 'TRY' | undefined,
        dateFrom: dateFrom ? new Date(dateFrom) : undefined,
        dateTo: dateTo ? new Date(dateTo) : undefined,
      });
      const safe = (res.cari.companyName || 'cari').replace(/[\\/?*[\]:]/g, ' ').slice(0, 24);
      const doc = buildDoc(res);
      const base = `trading-cari-ekstre-${safe}-${dateStamp()}`;
      if (kind === 'pdf') await downloadMovementsPdf(doc, `${base}.pdf`);
      else await downloadMovementsXlsx(doc, `${base}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading } = trpc.tradingCari.movements.useQuery(
    {
      source: src,
      id: id!,
      currency: (currency || undefined) as 'EUR' | 'GBP' | 'USD' | 'TRY' | undefined,
      dateFrom: dateFrom ? new Date(dateFrom) : undefined,
      dateTo: dateTo ? new Date(dateTo) : undefined,
      page,
    },
    { enabled: !!id }
  );

  const del = trpc.tradingCari.deleteMovement.useMutation({
    onSuccess: () => {
      utils.tradingCari.movements.invalidate();
      showSuccessToast(t('cariMovements.deleted', 'Movement deleted'));
    },
    onError: (e) => showErrorToast(e.message),
  });

  const fmt = (n: number) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n
    );

  const items = data?.items ?? [];
  const pag = data?.pagination;
  const colCount = canDelete ? 10 : 9;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {data?.cari.companyName ?? t('cariMovements.title', 'Account Ledger')}
            </h1>
            <p className="text-muted-foreground">
              {t('cariMovements.subtitle', 'Current-account movements')}
              {data?.cari.legacyCariCode ? ` · ${data.cari.legacyCariCode}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting || items.length === 0}>
                  {exporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {t('cariMovements.export', 'Export')}
                  <ChevronDown className="ml-2 h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => runExport('pdf')}>
                  <FileText className="h-4 w-4 text-red-600" />
                  {t('cariMovements.exportPdf', 'PDF')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => runExport('xlsx')}>
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  {t('cariMovements.exportExcel', 'Excel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && id && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('cariMovements.addMovement', 'Add Movement')}
            </Button>
          )}
        </div>
      </div>

      {/* Per-currency balance summary */}
      {data && data.summaryByCurrency.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.summaryByCurrency.map((s) => {
            const net = s.currency === 'TRY' ? s.tryNet : s.fxNet;
            const debit = s.currency === 'TRY' ? s.tryDebit : s.fxDebit;
            const credit = s.currency === 'TRY' ? s.tryCredit : s.fxCredit;
            return (
              <Card key={s.currency}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">
                    {s.currency} {t('cariMovements.balance', 'Balance')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className={`text-2xl font-bold tabular-nums ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {fmt(net)}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t('cariMovements.debit', 'Borç')} {fmt(debit)} ·{' '}
                    {t('cariMovements.credit', 'Alacak')} {fmt(credit)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">{t('cariMovements.dateFrom', 'From')}</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariMovements.dateTo', 'To')}</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariMovements.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">{t('cariMovements.allCurrencies', 'All')}</option>
            {['TRY', 'EUR', 'GBP', 'USD'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {(dateFrom || dateTo || currency) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setCurrency('');
              setPage(1);
            }}
          >
            {t('cariMovements.clear', 'Clear')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">
                {t('cariMovements.date', 'Tarih')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cariMovements.voucherNo', 'Fiş No')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cariMovements.description', 'Açıklama')}
              </th>
              <th className="px-3 py-3 text-center font-medium">
                {t('cariMovements.currency', 'Döviz')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariMovements.fxDebit', 'Döviz Borç')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariMovements.fxCredit', 'Döviz Alacak')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariMovements.tryDebit', 'TL Borç')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariMovements.tryCredit', 'TL Alacak')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariMovements.tryBalance', 'TL Bakiye')}
              </th>
              {canDelete && <th className="px-3 py-3" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={colCount} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="text-muted-foreground py-8 text-center">
                  {t('cariMovements.noEntries', 'No movements found')}
                </td>
              </tr>
            )}
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-muted/30 border-b last:border-0">
                <td className="whitespace-nowrap px-3 py-2.5">
                  {new Date(m.date).toLocaleDateString('tr-TR')}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">
                  {m.voucherNo}
                  {m.isManual && (
                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                      M
                    </span>
                  )}
                  {m.isOpeningCarryForward && (
                    <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                      {t('cariMovements.carryForward', 'Devir')}
                    </span>
                  )}
                </td>
                <td className="max-w-[280px] px-3 py-2.5">
                  <span className="block truncate">{m.description}</span>
                </td>
                <td className="px-3 py-2.5 text-center">{m.currency}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxDebit > 0 ? fmt(m.fxDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxCredit > 0 ? fmt(m.fxCredit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryDebit > 0 ? fmt(m.tryDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryCredit > 0 ? fmt(m.tryCredit) : ''}
                </td>
                <td
                  className={`px-3 py-2.5 text-right font-medium tabular-nums ${m.tryBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {fmt(m.tryBalance)}
                </td>
                {canDelete && (
                  <td className="px-3 py-2.5 text-right">
                    {m.isManual && (
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              t('cariMovements.confirmDelete', 'Delete this movement?')
                            )
                          ) {
                            del.mutate({ id: m.id });
                          }
                        }}
                        className="text-muted-foreground hover:text-red-600"
                        title={t('cariMovements.delete', 'Delete')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pag && pag.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {(page - 1) * pag.pageSize + 1}-{Math.min(page * pag.pageSize, pag.total)} / {pag.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('cariMovements.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pag.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('cariMovements.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      {id && (
        <AddMovementDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          source={src}
          id={id}
          counterpartyName={data?.cari.companyName ?? ''}
        />
      )}
    </div>
  );
}
